export const clamp01 = (v: number): number => (v > 0 ? (v < 1 ? v : 1) : 0);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const easeInOutCubic = (x: number): number => {
  const t = clamp01(x);
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};
export const easeOutCubic = (x: number): number => 1 - Math.pow(1 - clamp01(x), 3);
export const smoothstep = (a: number, b: number, x: number): number => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};

export const WAVE = 0.42;

export const riseAt = (t: number, col: number, cols: number, row: number, rows = 7): number => {
  const d = (cols > 1 ? col / (cols - 1) : 0) * 0.36 + (rows > 1 ? row / (rows - 1) : 0) * 0.06;
  return easeOutCubic((t - d) / (1 - WAVE));
};

export type Cam = { cs: number; sn: number; se: number; ce: number };
export type CamPose = { yaw: number; elev: number };

const deg = (v: number): number => (v * Math.PI) / 180;

export const TOP_DOWN_POSE: CamPose = { yaw: 0, elev: Math.PI / 2 };
export const SIDE_POSE: CamPose = { yaw: 0, elev: 0 };
export const ISO_POSE: CamPose = { yaw: Math.PI / 4, elev: deg(34) };
export const BAR_ISO_POSE: CamPose = { yaw: deg(28), elev: deg(30) };
export const SCATTER_ISO_POSE: CamPose = { yaw: deg(30), elev: deg(26) };

export type ProjectionKind = "bar" | "scatter" | "heatmap" | "quadrant";
export type FlatBase = "side" | "top";

export interface ProjectionPoses {
  flat: CamPose;
  iso: CamPose;
  base: FlatBase;
}

export const PROJECTION_POSES: Record<ProjectionKind, ProjectionPoses> = {
  bar: { flat: SIDE_POSE, iso: BAR_ISO_POSE, base: "side" },
  scatter: { flat: SIDE_POSE, iso: SCATTER_ISO_POSE, base: "side" },
  heatmap: { flat: TOP_DOWN_POSE, iso: ISO_POSE, base: "top" },
  quadrant: { flat: TOP_DOWN_POSE, iso: ISO_POSE, base: "top" },
};

export const cameraFromPose = (pose: CamPose): Cam => ({
  cs: Math.cos(pose.yaw),
  sn: Math.sin(pose.yaw),
  se: Math.sin(pose.elev),
  ce: Math.cos(pose.elev),
});

export const cameraBetween = (from: CamPose, to: CamPose, e: number): Cam => {
  const t = clamp01(e);
  return cameraFromPose({ yaw: lerp(from.yaw, to.yaw, t), elev: lerp(from.elev, to.elev, t) });
};

export const project = (c: Cam, x: number, y: number, z: number): [number, number] => [
  x * c.cs - y * c.sn,
  (x * c.sn + y * c.cs) * c.se - z * c.ce,
];

export const depthOf = (c: Cam, x: number, y: number): number => x * c.sn + y * c.cs;

export const FACE_FRONT = 1;
export const FACE_SIDE = 2;
export const FACE_MIN_PX = 0.35;

export const boxFaces = (c: Cam, s: number, z: number, wx: number, wy: number): number => {
  const tall = z * c.ce * s;
  let f = 0;
  if (tall > FACE_MIN_PX && wx * c.cs * s > FACE_MIN_PX) f |= FACE_FRONT;
  if (tall > FACE_MIN_PX && wy * c.sn * s > FACE_MIN_PX) f |= FACE_SIDE;
  return f;
};

export type Bounds = { minx: number; maxx: number; miny: number; maxy: number };

export const emptyBounds = (): Bounds => ({ minx: Infinity, maxx: -Infinity, miny: Infinity, maxy: -Infinity });

export const addPoint = (b: Bounds, cam: Cam, x: number, y: number, z: number): void => {
  const p = project(cam, x, y, z);
  if (p[0] < b.minx) b.minx = p[0];
  if (p[0] > b.maxx) b.maxx = p[0];
  if (p[1] < b.miny) b.miny = p[1];
  if (p[1] > b.maxy) b.maxy = p[1];
};

export const boxBounds = (cam: Cam, x: number, y: number, z: number): Bounds => {
  const b = emptyBounds();
  for (const xx of [0, x]) for (const yy of [0, y]) for (const zz of [0, z]) addPoint(b, cam, xx, yy, zz);
  return b;
};

export const aspectOf = (b: Bounds): number =>
  b.maxx > b.minx && Number.isFinite(b.maxx) ? (b.maxy - b.miny) / (b.maxx - b.minx) : 0;

export const BAR_GROUP_WIDTH = 0.72;
export const BAR_GROUP_GAP = 0.06;
export const BAR_FOOTPRINT = 0.62;

export type BarFootprint = { x0: number; x1: number; y0: number; y1: number };

export const barRow = (series: number, seriesCount: number): number => seriesCount - 1 - series;

export const barBox = (category: number, series: number, seriesCount: number, e: number): BarFootprint => {
  const n = Math.max(1, seriesCount);
  const gap = n > 1 ? BAR_GROUP_GAP : 0;
  const w2 = (BAR_GROUP_WIDTH - gap * (n - 1)) / n;
  const x2 = category + (1 - BAR_GROUP_WIDTH) / 2 + series * (w2 + gap);
  const x3 = category + (1 - BAR_FOOTPRINT) / 2;
  const y0 = barRow(series, n) + (1 - BAR_FOOTPRINT) / 2;
  return { x0: lerp(x2, x3, e), x1: lerp(x2 + w2, x3 + BAR_FOOTPRINT, e), y0, y1: y0 + BAR_FOOTPRINT };
};

export const barRiseAt = (t: number, category: number, categories: number, series: number, seriesCount: number): number =>
  riseAt(t, category, categories, seriesCount > 1 ? (barRow(series, seriesCount) / (seriesCount - 1)) * 6 : 0);

export const SKYLINE_CELLS = 53 * 7;
export const SLAB_HEIGHT = 0.2;

export const gridHeightScale = (cols: number, rows: number): number =>
  Math.min(1, Math.max(0.3, Math.sqrt(Math.max(1, cols * rows) / SKYLINE_CELLS)));

export const cellHeight = (value: number, max: number, scale = 1): number =>
  value > 0 && max > 0 ? 0.4 + Math.pow(Math.min(1, value / max), 0.85) * 7.2 * scale : SLAB_HEIGHT;

export const cellWidth = (e: number): number => lerp(0.78, 0.9, e);

export type Box3 = { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number };

export interface ProjectionFrame {
  width: number;
  height: number;
  depth: number;
  zExtent: number;
  poses: ProjectionPoses;
  pad: number;
}

export interface ViewTransform {
  e: number;
  cam: Cam;
  scale: number;
  tx: number;
  ty: number;
}

export const DEFAULT_PROJECTION_PAD = 12;

export const sceneBounds = (cam: Cam, frame: ProjectionFrame): Bounds =>
  boxBounds(cam, Math.max(0, frame.width), Math.max(0, frame.depth), Math.max(0, frame.zExtent));

export const containFit = (
  b: Bounds,
  width: number,
  height: number,
  pad: number,
): { scale: number; tx: number; ty: number } => {
  const availW = Math.max(1, width - 2 * pad);
  const availH = Math.max(1, height - 2 * pad);
  const bw = Number.isFinite(b.maxx) ? b.maxx - b.minx : 0;
  const bh = Number.isFinite(b.maxy) ? b.maxy - b.miny : 0;
  const sx = bw > 0 ? availW / bw : Infinity;
  const sy = bh > 0 ? availH / bh : Infinity;
  const raw = Math.min(sx, sy);
  const scale = Number.isFinite(raw) ? raw : 1;
  const minx = Number.isFinite(b.minx) ? b.minx : 0;
  const miny = Number.isFinite(b.miny) ? b.miny : 0;
  return {
    scale,
    tx: pad + (availW - bw * scale) / 2 - minx * scale,
    ty: pad + (availH - bh * scale) / 2 - miny * scale,
  };
};

export const flatTransform = (frame: ProjectionFrame): { scale: number; tx: number; ty: number } => ({
  scale: 1,
  tx: 0,
  ty: frame.poses.base === "side" ? frame.height : 0,
});

export const viewTransform = (frame: ProjectionFrame, e: number): ViewTransform => {
  const t = clamp01(e);
  const cam = cameraBetween(frame.poses.flat, frame.poses.iso, t);
  const flat = flatTransform(frame);
  if (t === 0) return { e: t, cam, ...flat };
  const pad = frame.pad * t;
  const b = sceneBounds(cam, frame);
  const fit = containFit(b, frame.width, frame.height, pad);
  const scale = Math.min(lerp(flat.scale, fit.scale, t), fit.scale);
  return {
    e: t,
    cam,
    scale,
    tx: clampOffset(lerp(flat.tx, fit.tx, t), b.minx * scale, b.maxx * scale, pad, frame.width - pad),
    ty: clampOffset(lerp(flat.ty, fit.ty, t), b.miny * scale, b.maxy * scale, pad, frame.height - pad),
  };
};

const clampOffset = (offset: number, lo: number, hi: number, min: number, max: number): number => {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return offset;
  if (lo + offset < min) return min - lo;
  if (hi + offset > max) return max - hi;
  return offset;
};

export const projectView = (v: ViewTransform, x: number, y: number, z: number): [number, number] => {
  const p = project(v.cam, x, y, z);
  return [p[0] * v.scale + v.tx, p[1] * v.scale + v.ty];
};

export const fmt = (v: number): string => {
  const r = Math.round(v * 100) / 100;
  return String(r === 0 ? 0 : r);
};

export const polygonPath = (points: ReadonlyArray<readonly [number, number]>): string => {
  if (points.length === 0) return "";
  let d = `M${fmt(points[0][0])} ${fmt(points[0][1])}`;
  for (let i = 1; i < points.length; i++) d += `L${fmt(points[i][0])} ${fmt(points[i][1])}`;
  return `${d}Z`;
};

export const segmentsPath = (
  segments: ReadonlyArray<readonly [readonly [number, number], readonly [number, number]]>,
): string => {
  let d = "";
  for (const [a, b] of segments) d += `M${fmt(a[0])} ${fmt(a[1])}L${fmt(b[0])} ${fmt(b[1])}`;
  return d;
};

export interface BoxPaths {
  top: string;
  front: string;
  side: string;
  faces: number;
}

export const boxPaths = (v: ViewTransform, b: Box3): BoxPaths => {
  const p = (x: number, y: number, z: number) => projectView(v, x, y, z);
  const top = polygonPath([p(b.x0, b.y0, b.z1), p(b.x1, b.y0, b.z1), p(b.x1, b.y1, b.z1), p(b.x0, b.y1, b.z1)]);
  const front = polygonPath([p(b.x0, b.y1, b.z0), p(b.x1, b.y1, b.z0), p(b.x1, b.y1, b.z1), p(b.x0, b.y1, b.z1)]);
  const side = polygonPath([p(b.x1, b.y0, b.z0), p(b.x1, b.y1, b.z0), p(b.x1, b.y1, b.z1), p(b.x1, b.y0, b.z1)]);
  const faces = boxFaces(v.cam, v.scale, b.z1 - b.z0, b.x1 - b.x0, b.y1 - b.y0);
  return { top, front, side, faces };
};

export const boxTopCenter = (v: ViewTransform, b: Box3): [number, number] =>
  projectView(v, (b.x0 + b.x1) / 2, (b.y0 + b.y1) / 2, b.z1);

export interface Projector {
  frame: ProjectionFrame;
  at(e: number): ViewTransform;
  project(x: number, y: number, z: number, e: number): [number, number];
  projectBox(b: Box3, e: number): BoxPaths;
}

export const createProjector = (frame: ProjectionFrame): Projector => {
  let last: ViewTransform | null = null;
  const at = (e: number): ViewTransform => {
    const t = clamp01(e);
    if (last && last.e === t) return last;
    last = viewTransform(frame, t);
    return last;
  };
  return {
    frame,
    at,
    project: (x, y, z, e) => projectView(at(e), x, y, z),
    projectBox: (b, e) => boxPaths(at(e), b),
  };
};

export const painterKey = (xIndex: number, yIndex: number): number => xIndex + yIndex;

export const painterOrder = (keys: ReadonlyArray<number>): number[] =>
  keys
    .map((key, index) => ({ key, index }))
    .sort((a, b) => a.key - b.key || a.index - b.index)
    .map((item) => item.index);

export const MAX_PROJECTED_BOXES = 1200;
export const MAX_PROJECTED_POINTS = 600;
